/**
 * Shared breadcrumb-trail utilities used by Admin / Member / Professional dashboards.
 *
 * The trail is a small comma-separated list of section IDs that records the user's
 * navigation path inside a dashboard. It lives in two places at once:
 *   1. URL `?trail=a,b,c` — so links, bookmarks, and back/forward all preserve it
 *   2. sessionStorage `<scope>:trail` — so a full-page refresh (or URL edit) on a
 *      deeply-nested page still keeps the chain visible
 *
 * Each dashboard passes its own `scope` (e.g. "md.admin.trail") so trails don't bleed
 * across role workspaces.
 */

const MAX_TRAIL = 4;

/** Parse a raw trail string (from URL or storage) into a clean array of section IDs.
 * Drops empty entries, drops the current section, and de-dupes adjacent repeats. */
export const parseTrail = (raw, currentSection) => {
  if (!raw) return [];
  const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
  const out = [];
  for (const p of parts) {
    if (p === currentSection) continue;
    if (out[out.length - 1] === p) continue;
    out.push(p);
  }
  return out.slice(-MAX_TRAIL);
};

/** Build a new trail by taking an existing one + appending an origin section. */
export const buildTrail = (existingRaw, originSection, nextSection) => {
  const existing = parseTrail(existingRaw, nextSection);
  const last = existing[existing.length - 1];
  if (originSection && originSection !== nextSection && originSection !== last) {
    existing.push(originSection);
  }
  return existing.slice(-MAX_TRAIL).join(",");
};

/** Read the persisted trail for a scope. Returns "" if not set. */
export const readPersistedTrail = (scope) => {
  try { return window.sessionStorage.getItem(scope) || ""; } catch (e) { return ""; }
};

/** Persist the trail for a scope. Silently no-ops when sessionStorage is unavailable. */
export const writePersistedTrail = (scope, trail) => {
  try {
    if (trail) window.sessionStorage.setItem(scope, trail);
    else window.sessionStorage.removeItem(scope);
  } catch (e) { /* private mode, quota, etc. */ }
};

/** Choose the most-specific trail source: URL > sessionStorage. The URL always
 * wins when present so links/bookmarks remain the source of truth. */
export const resolveTrail = ({ urlTrail, urlFrom, scope }) => {
  if (urlTrail) return urlTrail;
  if (urlFrom) return urlFrom; // legacy ?from=X single-hop
  return readPersistedTrail(scope);
};
